import { Suspense, useState } from "react";
import { Link as RouterLink, Outlet } from "react-router-dom";
import { Box, Typography, darken } from "@mui/material";
import useInterfaceConfig from "@hooks/useInterfaceConfigV2";
import { getStaticResourceUrl } from "@helpers/funciones";
import { DialogErrorV2 } from "@components/ui_v2/DialogErrorV2";
import { PATHS_COTIZADORES_V2 } from "@helpers/constantes";

/** Alto del logo en el panel de marca; mismo criterio de alto fijo que el sidebar del wizard. */
const LOGO_HEIGHT = 44;

/** Alto del logo arriba del formulario en móvil. */
const MOBILE_LOGO_HEIGHT = 32;

/**
 * Layout de las pantallas de acceso v2 (login y recuperación de contraseña): panel de marca a la
 * izquierda con el mismo fondo oscuro del sidebar (`primary.main` oscurecido), el logo para fondo
 * oscuro y el primer mensaje de `lateral.text_list` de la config; a la derecha, la columna del
 * formulario. En móvil solo queda la columna del formulario.
 */
const AuthLayoutV2 = () => {
  const interfaceConfig = useInterfaceConfig();
  const [isLogoLoading, setIsLogoLoading] = useState(true);

  const logo = getStaticResourceUrl(interfaceConfig?.lateral?.logo_bg_dark || interfaceConfig?.navbar.logo);
  const mobileLogo = getStaticResourceUrl(interfaceConfig?.navbar.logo);
  const tagline = interfaceConfig?.lateral?.text_list?.[0];

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#F6F8FB" }}>
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          justifyContent: "space-between",
          width: "42%",
          maxWidth: 560,
          p: "40px 48px",
          bgcolor: (theme) => darken(theme.palette.primary.main, 0.38),
        }}
      >
        {/* El logo regresa al cotizador del tenant (`CotizadoresV2` cae al legacy si no está migrado). */}
        <Box
          component={RouterLink}
          to={PATHS_COTIZADORES_V2.credito_simple.path}
          aria-label="Ir al cotizador"
          data-testid="auth-logo-link"
          sx={{ alignSelf: "flex-start", display: "inline-flex", maxWidth: "100%" }}
        >
          <Box
            component="img"
            src={logo}
            alt="logo"
            onLoad={() => setIsLogoLoading(false)}
            sx={{
              height: LOGO_HEIGHT,
              width: "auto",
              maxWidth: "100%",
              objectFit: "contain",
              display: isLogoLoading ? "none" : "block",
            }}
          />
        </Box>
        {tagline && (
          <Typography
            sx={{
              // 30px: titular del panel de marca, fuera de la escala de formularios del theme.
              fontSize: 30,
              fontWeight: 600,
              letterSpacing: "-0.02em",
              lineHeight: 1.2,
              color: "#fff",
              maxWidth: 380,
            }}
          >
            {tagline}
          </Typography>
        )}
        <Box />
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 3, md: 5 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 440 }}>
          {/* Móvil: el panel de marca se oculta, así que el logo (con el mismo enlace al cotizador) va
              arriba del formulario. Sobre el fondo claro se usa el logo normal del navbar. */}
          <Box
            component={RouterLink}
            to={PATHS_COTIZADORES_V2.credito_simple.path}
            aria-label="Ir al cotizador"
            data-testid="auth-logo-link-mobile"
            sx={{ display: { xs: "inline-flex", md: "none" }, maxWidth: "100%", mb: 3 }}
          >
            <Box
              component="img"
              src={mobileLogo}
              alt="logo"
              sx={{ height: MOBILE_LOGO_HEIGHT, width: "auto", maxWidth: "100%", objectFit: "contain" }}
            />
          </Box>
          <Suspense fallback={<></>}>
            <Outlet />
          </Suspense>
        </Box>
      </Box>
      <DialogErrorV2 />
    </Box>
  );
};

export default AuthLayoutV2;
